const VISIBLE_TOKEN_LENGTH = 15
const MAX_DEPTH = 5

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') {
    return false
  }
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

/**
 * Reads values through their descriptors so accessor properties are never invoked:
 * a getter may throw, or hand out an object that must not be mutated through it.
 */
const writableEntries = (target: object) =>
  Object.entries(Object.getOwnPropertyDescriptors(target))
    .filter(([, descriptor]) => descriptor.enumerable === true && descriptor.writable === true)
    .map(([key, descriptor]): [string, unknown] => [key, descriptor.value])

/**
 * Masks every string reachable through plain objects and arrays, in place.
 *
 * Class instances and accessor properties are skipped on purpose: a Sentry transaction event
 * carries the live `Scope` and `Client` under `sdkProcessingMetadata`, and writing to a
 * getter-only property there (e.g. the client's promise buffer `$`) throws. None of that
 * is ever sent, so leaving it untouched loses nothing.
 */
export const maskDeepMutate = (
  value: unknown,
  maskFn: (str: string) => string,
  level = 1
): unknown => {
  if (level > MAX_DEPTH) {
    return value
  }

  if (typeof value === 'string') {
    return maskFn(value)
  }

  if (Array.isArray(value) || isPlainObject(value)) {
    writableEntries(value).forEach(([key, item]) => {
      const masked = maskDeepMutate(item, maskFn, level + 1)
      if (masked !== item) {
        Reflect.set(value, key, masked)
      }
    })
    return value
  }

  return value
}

export const maskToken = (token: string): string => {
  if (token.length <= VISIBLE_TOKEN_LENGTH) {
    return token
  }

  const visiblePart = token.slice(0, VISIBLE_TOKEN_LENGTH)
  const maskLength = token.length - VISIBLE_TOKEN_LENGTH

  return visiblePart + 'X'.repeat(maskLength)
}

/** Masks a token carried as a `/token/<value>` path segment or a `?token=<value>` query param. */
export const maskTokenInUrl = (url: string) =>
  url
    .replace(/\/token\/([^/?#]+)/, (_, token) => `/token/${maskToken(token)}`)
    .replace(/([?&]token=)([^&#]+)/, (_, prefix, token) => `${prefix}${maskToken(token)}`)
